import { getTitleBannerImage } from "@west-marches/shared";

/**
 * The banner artwork has visible top/bottom edge strokes plus a flat inner
 * area. These constants describe how text should sit inside the banner
 * artwork (relative to the banner's natural rendered height) and how much
 * extra horizontal padding to leave around the text.
 *
 * Values were tuned visually for the "Title Banner 6" scroll artwork.
 */
const BANNER_INNER_HEIGHT_RATIO = 0.72;
const BANNER_HORIZONTAL_PADDING_FACTOR = 1.0;
const BANNER_EXTRA_HEIGHT_PER_ADDITIONAL_LINE = 0.2;
const BANNER_EXTRA_CHUNKS_PER_ADDITIONAL_LINE = 3;
// Each extra line shifts the banner upward (subtracting from the base
// vertical offset, allowing a small negative result so two-line banners sit
// slightly above the text centre instead of being dragged down by it).
const BANNER_VERTICAL_OFFSET_REDUCTION_PER_ADDITIONAL_LINE = 0.07;
/**
 * Vertical nudge applied to the banner relative to the text centre, expressed
 * as a fraction of the banner's rendered height. Positive values move the
 * banner downward so the text sits slightly above the banner's centre line.
 */
const BANNER_VERTICAL_OFFSET_RATIO = 0.05;

export interface TextBannerLayout {
  /** Total banner width in world units. */
  width: number;
  /** Total banner height in world units. */
  height: number;
  /** Number of mid chunks tiled between left and right caps. */
  midCount: number;
  /** Uniform scale applied to the natural banner artwork. */
  scale: number;
  /**
   * Vertical offset (positive = downward) of the banner relative to the text
   * centre. The drawing helpers apply this; layout consumers (e.g. selection
   * bounds) should add it when computing the banner's bounding box.
   */
  verticalOffset: number;
}

interface TextMetrics {
  width: number;
  height: number;
  fontSize: number;
  lineCount?: number;
}

/**
 * Returns the layout for a banner that fits the given text metrics. Returns
 * `null` when banner artwork has not finished loading yet — callers should
 * skip drawing in that case (the canvas will redraw once images load).
 */
export function computeTextBannerLayout(text: TextMetrics): TextBannerLayout | null {
  const left = getTitleBannerImage("left");
  const mid = getTitleBannerImage("mid");
  const right = getTitleBannerImage("right");
  if (!left || !mid || !right || !left.naturalWidth || !mid.naturalWidth || !right.naturalWidth) {
    return null;
  }

  const naturalHeight = Math.max(left.naturalHeight, mid.naturalHeight, right.naturalHeight);
  if (naturalHeight <= 0) {
    return null;
  }

  const lineCount = Math.max(1, text.lineCount ?? 1);
  const multilineScaleBoost =
    1 + Math.max(0, lineCount - 1) * BANNER_EXTRA_HEIGHT_PER_ADDITIONAL_LINE;
  const desiredBannerHeight =
    (text.height / BANNER_INNER_HEIGHT_RATIO) * multilineScaleBoost;
  const scale = desiredBannerHeight / naturalHeight;

  const leftWidth = left.naturalWidth * scale;
  const midWidth = mid.naturalWidth * scale;
  const rightWidth = right.naturalWidth * scale;
  const horizontalPadding = text.fontSize * BANNER_HORIZONTAL_PADDING_FACTOR;

  const requiredInnerWidth = Math.max(
    0,
    text.width + horizontalPadding * 2 - leftWidth - rightWidth,
  );
  const baseMidCount = Math.max(1, Math.ceil(requiredInnerWidth / midWidth));
  const midCount =
    baseMidCount +
    Math.max(0, lineCount - 1) * BANNER_EXTRA_CHUNKS_PER_ADDITIONAL_LINE;

  const width = leftWidth + midCount * midWidth + rightWidth;
  const height = naturalHeight * scale;

  return {
    width,
    height,
    midCount,
    scale,
    verticalOffset:
      height *
      (BANNER_VERTICAL_OFFSET_RATIO -
        Math.max(0, lineCount - 1) *
          BANNER_VERTICAL_OFFSET_REDUCTION_PER_ADDITIONAL_LINE),
  };
}

/**
 * Draws the title banner centred at (0, 0) in the current canvas transform.
 * Returns false if the banner could not be drawn yet (artwork still loading).
 */
export function drawTextBanner(
  ctx: CanvasRenderingContext2D,
  text: TextMetrics,
): TextBannerLayout | null {
  const layout = computeTextBannerLayout(text);
  if (!layout) return null;

  const left = getTitleBannerImage("left");
  const mid = getTitleBannerImage("mid");
  const right = getTitleBannerImage("right");
  if (!left || !mid || !right) return null;

  const leftWidth = left.naturalWidth * layout.scale;
  const midWidth = mid.naturalWidth * layout.scale;
  const rightWidth = right.naturalWidth * layout.scale;

  const startX = -layout.width / 2;
  const topY = -layout.height / 2 + layout.verticalOffset;

  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(left, startX, topY, leftWidth, layout.height);
  let cursor = startX + leftWidth;
  for (let i = 0; i < layout.midCount; i++) {
    ctx.drawImage(mid, cursor, topY, midWidth, layout.height);
    cursor += midWidth;
  }
  ctx.drawImage(right, cursor, topY, rightWidth, layout.height);

  ctx.restore();

  return layout;
}
